import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ShieldCheck, Sun, Flame, Wind, Activity, QrCode, PackageOpen,
  Sparkles, Star, MapPin, ChevronLeft, ChevronRight,
} from "lucide-react";
import { usePublic } from "@/hooks/usePublic";
import { useSite } from "@/context/SiteContext";
import { Reveal, SectionHeading, Eyebrow } from "@/components/public/ui";
import { mediaUrl } from "@/lib/media";

const RIDER = "https://images.unsplash.com/photo-1590506995460-d0d9892b54da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const LOBBY = "https://images.unsplash.com/photo-1718220268527-4477fd170775?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TECH = "https://images.unsplash.com/photo-1777886290085-f5cb4fbc0f6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const stats = [
  { v: "99.9%", l: "Germs Eliminated" },
  { v: "30s", l: "Per Cycle" },
  { v: "360°", l: "Protection" },
  { v: "100%", l: "Chemical Free" },
];

const features = [
  { icon: Sun, title: "UV-C Sterilization", body: "Medical-grade UV-C rays eliminate bacteria, viruses and pathogens." },
  { icon: Flame, title: "Heat Treatment", body: "Controlled heat removes sweat, moisture and odour at the source." },
  { icon: Wind, title: "Air Cleaning", body: "HEPA-grade airflow reaches every corner for a truly fresh finish." },
  { icon: Activity, title: "AI + RTM Monitoring", body: "Every cycle validated in real time — verifiable, auditable hygiene." },
];

const steps = [
  { icon: QrCode, title: "Scan the QR", body: "Scan the on-machine QR code and start a cycle in one tap." },
  { icon: PackageOpen, title: "Insert Helmet", body: "Place your helmet inside; smart sensors lock the optimal program." },
  { icon: Sparkles, title: "Auto-Clean & Go", body: "UV, heat and air run for ~30s. AI verifies the cycle — ready to wear." },
];

function Hero() {
  const { settings } = useSite();
  const { data } = usePublic("/sliders");
  const slides =
    data && data.length
      ? data
      : [
          {
            image: RIDER,
            title: "India's First AI Helmet Sanitization Machine",
            subtitle: "99.9% germ-free helmets in seconds. Chemical-free UV + Heat + Air technology, monitored in real time by AI.",
            cta_text: "Explore Products",
            cta_link: "/products",
          },
        ];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const s = slides[i] || slides[0];
  const go = (d) => setI((p) => (p + d + slides.length) % slides.length);

  return (
    <section className="relative min-h-[92vh] overflow-hidden bg-brand-navy" data-testid="hero">
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img src={mediaUrl(s.image) || RIDER} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </motion.div>
      </AnimatePresence>

      <div className="container-page relative flex min-h-[92vh] flex-col justify-center pt-24">
        <motion.div
          key={`c-${i}`}
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-2xl"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 backdrop-blur-sm">
            <ShieldCheck size={15} className="text-brand-orange" />
            <span className="text-xs font-600 tracking-wide text-white">{settings.tagline}</span>
          </div>
          <h1 className="font-display text-4xl font-800 leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {s.title}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-blue-100/90 sm:text-lg">{s.subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to={s.cta_link || "/products"}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 font-display font-700 text-white transition-all duration-200 hover:bg-brand-orange-dark hover:shadow-soft"
              data-testid="hero-cta"
            >
              {s.cta_text || "Explore Products"}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-3.5 font-display font-600 text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/15"
              data-testid="hero-enquire"
            >
              Request a Demo
            </Link>
          </div>
        </motion.div>
      </div>

      {slides.length > 1 && (
        <div className="absolute bottom-8 right-6 z-10 flex gap-2">
          <button onClick={() => go(-1)} className="grid h-11 w-11 place-items-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/15" data-testid="hero-prev"><ChevronLeft size={20} /></button>
          <button onClick={() => go(1)} className="grid h-11 w-11 place-items-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/15" data-testid="hero-next"><ChevronRight size={20} /></button>
        </div>
      )}
    </section>
  );
}

function StatsBand() {
  return (
    <section className="relative z-10 -mt-20">
      <div className="container-page">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-slate-100 shadow-soft lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-white p-7 text-center">
              <div className="font-display text-4xl font-800 text-brand">{s.v}</div>
              <div className="mt-2 text-sm font-600 uppercase tracking-wide text-muted-foreground">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutIntro() {
  const { settings } = useSite();
  return (
    <section className="container-page py-24">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-brand-sky p-6 shadow-card">
              <img src="/aimway-logo.png" alt="Aimway machine" className="mx-auto w-full max-w-sm" data-testid="about-img" />
            </div>
            <div className="absolute -bottom-6 -right-4 hidden rounded-2xl bg-brand-orange px-6 py-4 text-white shadow-soft sm:block">
              <div className="font-display text-2xl font-800">Made in India</div>
              <div className="text-xs opacity-90">Designed & engineered locally</div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <SectionHeading
            eyebrow="Who We Are"
            title="Aimway Technology Solutions"
            subtitle={settings.about_short}
          />
          <ul className="mt-8 space-y-4">
            {["Triple-action UV + Heat + Air sanitization", "AI + RTM real-time cycle monitoring", "Chemical-free, helmet-safe & eco-friendly", "Self-serve, contactless & ready in 30 seconds"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-sky text-brand"><ShieldCheck size={14} /></span>
                <span className="text-slate-700">{t}</span>
              </li>
            ))}
          </ul>
          <Link to="/about" className="mt-9 inline-flex items-center gap-2 font-display font-700 text-brand hover:gap-3 transition-all" data-testid="about-more">
            Learn more about us <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Technology() {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-24">
      <div className="container-page">
        <SectionHeading center eyebrow="Our Technology" title="Triple-action sanitization, watched by AI" subtitle="A first for India — every cycle validated in real time for guaranteed, verifiable hygiene." className="mb-14" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-slate-100 bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-soft">
                <div className="mb-6 grid h-14 w-14 place-items-center rounded-xl bg-brand-sky text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <f.icon size={24} />
                </div>
                <h3 className="font-display text-lg font-700 text-brand-navy">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const { data } = usePublic("/products");
  const products = (data || []).slice(0, 3);
  return (
    <section className="container-page py-24">
      <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading eyebrow="Our Products" title="Engineered for real-world hygiene" />
        <Link to="/products" className="inline-flex items-center gap-2 font-display font-700 text-brand hover:gap-3 transition-all">View all products <ArrowRight size={18} /></Link>
      </div>
      {products.length ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <Link to={`/products/${p.slug || p.id}`} className="group block h-full overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-soft" data-testid={`home-product-${i}`}>
                <div className="aspect-[4/3] overflow-hidden bg-brand-sky">
                  <img src={mediaUrl(p.image) || "/aimway-logo.png"} alt={p.name} className="h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-700 text-brand-navy">{p.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.short_desc || p.description}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-700 text-brand-orange">View details <ArrowRight size={16} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      ) : (
        <Reveal>
          <div className="grid grid-cols-1 items-center gap-8 rounded-2xl border border-slate-100 bg-white p-8 shadow-card md:grid-cols-2">
            <img src="/aimway-logo.png" alt="Aimway" className="mx-auto w-full max-w-xs" />
            <div>
              <h3 className="font-display text-2xl font-800 text-brand-navy">Helmet Multigear Sanitization Station</h3>
              <p className="mt-3 text-muted-foreground">360° protection with UV + Heat + Air cleaning. Chemical-free, self-serve and AI-monitored.</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-display font-700 text-white transition-colors hover:bg-brand-navy">Request a Quote <ArrowRight size={18} /></Link>
            </div>
          </div>
        </Reveal>
      )}
    </section>
  );
}

function Process() {
  return (
    <section className="bg-brand-navy py-24 text-white">
      <div className="container-page">
        <SectionHeading center eyebrow="How It Works" title="Three steps to a clean helmet" className="mb-14 [&_h2]:text-white [&_p]:text-blue-100/80" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="relative h-full rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                <span className="font-display text-5xl font-800 text-white/15">0{i + 1}</span>
                <s.icon size={28} className="absolute right-8 top-8 text-brand-orange" />
                <h3 className="mt-6 font-display text-xl font-700">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-blue-100/80">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationsCTA() {
  return (
    <section className="container-page py-24">
      <div className="grid grid-cols-1 items-center gap-10 overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-card lg:grid-cols-2">
        <div className="p-10 lg:p-14">
          <SectionHeading eyebrow="Find Us" title="Locate Aimway machines near you" subtitle="Our sanitization stations are being installed across petrol pumps, offices, malls and campuses. Find the nearest one or become a distributor." />
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/machine-locations" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-display font-700 text-white transition-colors hover:bg-brand-navy" data-testid="cta-locations"><MapPin size={18} /> Machine Locations</Link>
            <Link to="/find-distributor" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3 font-display font-700 text-brand-navy transition-colors hover:border-brand hover:text-brand">Find a Distributor</Link>
          </div>
        </div>
        <div className="relative h-full min-h-[280px]">
          <img src={LOBBY} alt="Aimway placement" className="absolute inset-0 h-full w-full object-cover" />
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { data } = usePublic("/testimonials");
  const items =
    data && data.length
      ? data
      : [
          { name: "Rahul Mehta", role: "Owner, Ride Rentals — Pune", quote: "Aimway instantly became a talking point at our counter. Customers trust us more knowing every helmet is sanitized.", rating: 5 },
          { name: "Sneha Kapoor", role: "Facility Manager, Bengaluru", quote: "The real-time monitoring is the clincher — I can show hygiene reports to our auditors.", rating: 5 },
          { name: "Arjun Nair", role: "Ops Lead, LastMile Logistics", quote: "Chemical-free and just 30 seconds a cycle. Our riders use it before every shift.", rating: 5 },
        ];

  const [i, setI] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const lg = window.matchMedia("(min-width: 1024px)");
    const sm = window.matchMedia("(min-width: 640px)");
    const update = () => setPerView(lg.matches ? 3 : sm.matches ? 2 : 1);
    update();
    lg.addEventListener("change", update);
    sm.addEventListener("change", update);
    return () => { lg.removeEventListener("change", update); sm.removeEventListener("change", update); };
  }, []);

  const max = Math.max(0, items.length - perView);
  useEffect(() => { setI((p) => Math.min(p, max)); }, [max]);

  useEffect(() => {
    if (items.length <= perView) return;
    const t = setInterval(() => setI((p) => (p >= max ? 0 : p + 1)), 5500);
    return () => clearInterval(t);
  }, [items.length, perView, max]);

  const cardW = 100 / perView;
  const canSlide = items.length > perView;

  return (
    <section className="bg-slate-50 py-24" data-testid="testimonials-section">
      <div className="container-page">
        <SectionHeading center eyebrow="Testimonials" title="Trusted on the ground" className="mb-14" />
        <div className="relative">
          <div className="overflow-hidden">
            <motion.div
              className="flex"
              animate={{ x: `-${i * cardW}%` }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              {items.map((t, idx) => (
                <div key={idx} className="shrink-0 px-3" style={{ flexBasis: `${cardW}%`, minWidth: `${cardW}%` }} data-testid={`testimonial-${idx}`}>
                  <div className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-8 shadow-card">
                    <div className="mb-4 flex gap-1 text-brand-orange">
                      {Array.from({ length: t.rating || 5 }).map((_, s) => <Star key={s} size={16} fill="currentColor" />)}
                    </div>
                    <p className="flex-1 text-[15px] leading-relaxed text-slate-700">&ldquo;{t.quote}&rdquo;</p>
                    <div className="mt-6 border-t border-slate-100 pt-5">
                      <div className="font-display font-700 text-brand-navy">{t.name}</div>
                      <div className="text-sm text-muted-foreground">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {canSlide && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button onClick={() => setI((p) => (p > 0 ? p - 1 : max))} className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white text-brand-navy transition-colors hover:border-brand hover:text-brand" aria-label="Previous" data-testid="testimonial-prev"><ChevronLeft size={18} /></button>
              <div className="flex items-center gap-1.5">
                {Array.from({ length: max + 1 }).map((_, d) => (
                  <button key={d} onClick={() => setI(d)} aria-label={`Slide ${d + 1}`} className={`h-2 rounded-full transition-all ${d === i ? "w-8 bg-brand" : "w-2 bg-slate-300"}`} />
                ))}
              </div>
              <button onClick={() => setI((p) => (p >= max ? 0 : p + 1))} className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white text-brand-navy transition-colors hover:border-brand hover:text-brand" aria-label="Next" data-testid="testimonial-next"><ChevronRight size={18} /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function GalleryPreview() {
  const { data } = usePublic("/gallery");
  const imgs = (data && data.length ? data.map((g) => mediaUrl(g.image)) : [RIDER, TECH, LOBBY, "/aimway-logo.png", RIDER, TECH, LOBBY, "/aimway-logo.png"]).slice(0, 8);
  return (
    <section className="container-page py-24">
      <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading eyebrow="Gallery" title="See Aimway in action" />
        <Link to="/gallery" className="inline-flex items-center gap-2 font-display font-700 text-brand hover:gap-3 transition-all">View gallery <ArrowRight size={18} /></Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {imgs.map((src, i) => (
          <Reveal key={i} delay={(i % 4) * 0.05}>
            <div className="aspect-square overflow-hidden rounded-xl border border-slate-100 bg-brand-sky">
              <img src={src} alt="" className="h-full w-full object-contain p-2 transition-transform duration-500 hover:scale-105" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ContactCTA() {
  return (
    <section className="relative overflow-hidden bg-brand py-20">
      <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-brand-orange/30 blur-3xl" />
      <div className="container-page relative flex flex-col items-center gap-8 text-center">
        <h2 className="max-w-2xl font-display text-3xl font-800 leading-tight text-white sm:text-4xl">Ready to upgrade your hygiene standards?</h2>
        <p className="max-w-xl text-blue-100">Join businesses across India trusting Aimway sanitization technology. Perfect for fleets, offices, sports facilities and public spaces.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-display font-700 text-brand transition-transform duration-200 hover:scale-105" data-testid="footer-cta">
          Get Started Today <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home">
      <Hero />
      <StatsBand />
      <AboutIntro />
      <Technology />
      <FeaturedProducts />
      <Process />
      <LocationsCTA />
      <Testimonials />
      <GalleryPreview />
      <ContactCTA />
    </div>
  );
}
